import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageSquareText } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { getBotMessages, saveBotMessages } from "@/lib/dentiumApi";
import { SettingsCard, SaveButton } from "@/components/settings/SettingsCard";

const VARS = ["{dias_cita}", "{clinica}", "{ciudad}", "{telefono}", "{direccion}", "{email}"];

const MessageField = ({ id, label, hint, value, preview, onChange }) => (
  <div>
    <Label className="mb-1 block">{label}</Label>
    <p className="text-xs text-[#8A7E70] mb-1.5">{hint}</p>
    <Textarea data-testid={`${id}-input`} value={value} rows={4} maxLength={1500} className="resize-none"
      onChange={(e) => onChange(e.target.value)} />
    {preview && (
      <div data-testid={`${id}-preview`} className="mt-2 rounded-xl bg-[#FAF8F5] border border-[#EDE7DE] px-3 py-2 text-xs text-[#6B6157] whitespace-pre-wrap">
        <span className="font-medium text-[#1A1A1A]">Vista previa: </span>{preview}
      </div>
    )}
  </div>
);

export const BotMessagesPanel = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["bot-messages"], queryFn: getBotMessages });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ welcome: data.welcome, out_of_hours: data.out_of_hours }); }, [data]);

  const save = useMutation({
    mutationFn: () => saveBotMessages(form),
    onSuccess: (d) => { qc.setQueryData(["bot-messages"], d); toast.success("Mensajes del bot guardados"); },
    onError: () => toast.error("No se pudieron guardar los mensajes"),
  });

  if (!form) return null;
  const valid = form.welcome.trim() && form.out_of_hours.trim();
  return (
    <SettingsCard icon={MessageSquareText} title="Mensajes del bot" testid="bot-messages-panel"
      description="Textos que el bot envía automáticamente. Puedes usar variables que se rellenan solas con los datos configurados.">
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="bot-messages-variables">
        {VARS.map((v) => (
          <code key={v} className="text-[11px] rounded-full bg-[#F1EBE1] text-[#6B5A43] px-2.5 py-1">{v}</code>
        ))}
        <span className="text-[11px] text-[#8A7E70] self-center ml-1">{"{dias_cita}"} = {data?.dias_cita}</span>
      </div>
      <div className="space-y-5">
        <MessageField id="welcome-message" label="Mensaje de bienvenida" hint="Se envía en el primer contacto del paciente."
          value={form.welcome} preview={data?.welcome_preview} onChange={(v) => setForm((s) => ({ ...s, welcome: v }))} />
        <MessageField id="out-of-hours-message" label="Mensaje fuera de horario" hint="Se envía cuando el paciente escribe fuera del horario de atención configurado."
          value={form.out_of_hours} preview={data?.out_of_hours_preview} onChange={(v) => setForm((s) => ({ ...s, out_of_hours: v }))} />
      </div>
      <div className="flex justify-end mt-5">
        <SaveButton testid="save-bot-messages-btn" pending={save.isPending} disabled={!valid} onClick={() => save.mutate()} />
      </div>
    </SettingsCard>
  );
};
